/**
 * 인피드 광고를 **어디에 끼울지**만 정하는 순수 계산 (수익모델 §3.1).
 *
 * 밀도는 화면마다 다르고, 문서가 정본이다:
 *
 *   SC-019a 요즘 많이 모으는·전체 : 9번째부터 · 이후 10개마다
 *   SC-006  세트 카드 목록        : 13번째부터 · 이후 12개마다
 *   SC-002  내 컬렉션(목록)        : 4번째부터 · 이후 10개마다
 *   SC-019  홈                    : §2 목록의 마지막 줄 (밀도 계산이 필요 없다)
 *
 * ⚠ 광고를 **안 넣는 자리**도 문서가 정해 두었다 — SC-007 카드 상세(데이터 존) ·
 *   SC-003 스캔 · SC-010 인식 결과 · SC-011 요약 · **SC-014 컬렉션 상세의 보유
 *   카드 목록**, 그리고 **첫 화면(스크롤 없이 보이는 범위)**. 그래서 start 가 늘
 *   한 화면 너머다 — 9·13 은 임의의 수가 아니라 '첫 화면을 넘긴 뒤' 라는 뜻이다.
 *
 * ⚠ **컬렉션은 두 층이고 판정이 갈린다** (2026-08-25 문서 개정).
 *   목록(SC-002, 폴더가 늘어선 층)은 허용, 그 **안의 보유 카드**(SC-014)는 금지 —
 *   폴더는 자산을 세는 자리가 아니지만 카드는 자산 그 자체다. 홈 §1·§3 캐러셀을
 *   뺀 것과 같은 판단축이다.
 *
 * 계산만 여기 두는 이유: 목록 컴포넌트마다 이 규칙을 다시 적으면 밀도를 바꿀 때
 * 화면 수만큼 고쳐야 한다. 테스트도 여기 하나로 끝난다.
 */

/** 목록 한 칸 — 진짜 항목이거나 광고 자리다 */
export type FeedEntry<T> =
  | { kind: "item"; item: T; key: string }
  | { kind: "ad"; key: string; /** 이 화면에서 몇 번째 광고인가 (1부터) */ index: number };

export interface AdDensity {
  /** 첫 광고가 들어갈 자리 — 이 번째 항목 **뒤**에 붙는다 (1부터) */
  start: number;
  /** 그다음부터 몇 개마다 */
  every: number;
}

/** SC-019a 요즘 많이 모으는 · 전체 */
export const AD_DENSITY_TRENDING: AdDensity = { start: 9, every: 10 };
/** SC-006 세트 카드 목록 */
export const AD_DENSITY_SET_CARDS: AdDensity = { start: 13, every: 12 };
/**
 * SC-002 내 컬렉션(목록).
 * 4번째부터인 이유 — 컬렉션이 3개 이하면 광고가 목록의 절반을 먹는다. 4개 이상
 * 만든 사용자는 이미 앱을 깊게 쓰는 사람이라 광고 한 줄에 떠나지 않는다.
 */
export const AD_DENSITY_COLLECTIONS: AdDensity = { start: 4, every: 10 };

/**
 * 항목 목록에 광고 자리를 끼워 넣는다.
 *
 * ⚠ 광고는 항목 **뒤**에 붙는다. 앞에 붙이면 목록이 광고로 시작한다.
 * ⚠ 마지막 항목 뒤에는 붙이지 않는다 — 목록의 끝이 광고면 '끝'이 안 보인다.
 */
export function withInFeedAds<T>(
  items: readonly T[],
  density: AdDensity,
  keyOf: (item: T, index: number) => string,
): FeedEntry<T>[] {
  const out: FeedEntry<T>[] = [];
  let adCount = 0;

  items.forEach((item, i) => {
    out.push({ kind: "item", item, key: keyOf(item, i) });

    const position = i + 1; // 1부터 센다
    const isLast = position === items.length;
    if (isLast) return;

    const afterStart = position - density.start;
    if (afterStart >= 0 && afterStart % density.every === 0) {
      adCount += 1;
      out.push({ kind: "ad", key: `ad-${position}`, index: adCount });
    }
  });

  return out;
}
